import {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import Environment from '../../../../../environment';
import {ArenaStandingEntry} from '@/Store/ArenaStore';

// ── useGetArenaStandings ─────────────────────────────────────────────────────
// Paged arena standings from the game service's REST surface:
//   GET {GAME_SERVICE_URL}/arenaStandings?arenaId=&page=&pageSize=&userId=
//   → {code, entries[], state, endsAtMs, counts, page, pageSize, total,
//      totalPages, me: {...entry, inPage} | null, archived}
//
// This exists ONLY for pages the socket does not cover. The standings
// broadcast pushes page 1 to the whole room every ~2s, so page 1 stays live
// off the socket and this hook is what lets a user browse deeper. Server-side
// it is redis-first with a postgres fallback, so an archived arena pages too.
//
// `me` is the caller's own row regardless of page (null for a spectator who
// never joined), with `inPage` telling the UI whether to float it.
// Auth mirrors the other game-service REST routes (cc-conn-mgr gsApiCheck):
// an `x-api-key` header. GAME_SERVICE_URL is the socket origin (ws(s)://), so
// REST rides the same host over http(s) — hence the scheme swap.

export interface ArenaStandingsMe extends ArenaStandingEntry {
  inPage: boolean;
}

export interface ArenaStandingsPage {
  entries: ArenaStandingEntry[];
  state: string;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  me: ArenaStandingsMe | null;
  archived: boolean;
}

const GAME_SERVICE_REST_BASE = String(
  Environment.GAME_SERVICE_URL || '',
).replace(/^ws/, 'http');

export default function useGetArenaStandings(
  arenaId?: string | number,
  page: number = 1,
  pageSize: number = 20,
  userId?: string | number,
  // The caller passes false for page 1 (the socket already covers it live) —
  // this hook must not duplicate that traffic.
  enabled: boolean = true,
) {
  const [data, setData] = useState<ArenaStandingsPage | null>(null);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (!arenaId || !GAME_SERVICE_REST_BASE || !enabled) {
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `${GAME_SERVICE_REST_BASE}/arenaStandings`,
        {
          params: {
            arenaId: String(arenaId),
            page: String(page),
            pageSize: String(pageSize),
            ...(userId ? {userId: String(userId)} : {}),
          },
          headers: {'x-api-key': Environment.X_API_KEY},
        },
      );
      if (response?.data?.code === 200) {
        setData(response.data as ArenaStandingsPage);
      }
    } catch (e: any) {
      // Keep the previous page rendered on a failed fetch rather than
      // blanking the leaderboard under the user.
      console.warn('arenaStandings fetch failed:', e?.message);
    } finally {
      setLoading(false);
    }
  }, [arenaId, page, pageSize, userId, enabled]);

  // Fetch on navigation only — deliberately NOT polled.
  // Everything that must be live already arrives by push: page 1 is broadcast
  // to the room on each game end (coalesced), and the caller's own rank/points
  // ride the 1:1 status push. A deep page is a lookup, not a live feed, so
  // polling it bought almost nothing while costing a full page rebuild every
  // tick — and scaled with READERS rather than with events, which is the wrong
  // shape. If a deep page ever needs to feel live, drive it off the broadcast
  // as a change signal rather than a timer.
  useEffect(() => {
    if (!enabled) {
      setData(null);
      return;
    }
    refetch();
  }, [enabled, refetch]);

  return {data, loading, refetch};
}
